import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, Copy, InfoIcon, X } from 'lucide-react';

import { useCreateAPICredential } from '@/api/APICredentials/useCreateAPICredential';
import { brandColors } from '@/app/constants';
import { Button } from '@/component-library/primitives/Button/Button';
import { Textarea } from '@/component-library/primitives/Textarea/Textarea';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import { Input } from '@/component-library/widgets/Input/Input';

interface CreateAPICredentialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface CreatedCredential {
  id: string;
  name: string;
  description?: string;
  workspaceId: string;
  siteId: string;
  apiKey: string;
  expiresAt: string | null;
  createdAt: string;
  lastUsed: string;
  createdBy: string;
}

const CreateAPICredentialModal: React.FC<CreateAPICredentialModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [expirationDays, setExpirationDays] = useState<string>('');
  const [createdCredential, setCreatedCredential] = useState<CreatedCredential | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedEnvLine, setCopiedEnvLine] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; description?: string; expirationDays?: string }>({});
  const [open, setOpen] = useState(false);
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' });

  const { mutate: createAPICredential, isLoading } = useCreateAPICredential();

  if (!isOpen) return null;

  const handleClose = () => {
    if (!isLoading) {
      setName('');
      setDescription('');
      setExpirationDays('');
      setCreatedCredential(null);
      setErrors({});
      setCopied(false);
      setCopiedEnvLine(false);
      onClose();
    }
  };

  const handleSubmit = () => {
    const newErrors: { name?: string; description?: string; expirationDays?: string } = {};

    // Validate name
    if (!name.trim()) {
      newErrors.name = t('CommonErrors.nameRequired');
    } else if (name.trim().length > 255) {
      newErrors.name = 'Name must be 255 characters or less';
    }

    // Validate description
    if (description.trim().length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    // Validate expiration days
    if (expirationDays.trim()) {
      const days = parseInt(expirationDays, 10);
      if (isNaN(days) || days <= 0 || !Number.isInteger(days)) {
        newErrors.expirationDays = 'Expiration days must be a positive integer';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    createAPICredential(
      {
        name: name.trim(),
        description: description.trim() || undefined,
        expirationDays: expirationDays.trim() ? parseInt(expirationDays, 10) : undefined,
      },
      {
        onSuccess: (response) => {
          setCreatedCredential(response.data);
        },
        onError: (err: any) => {
          const errorMessage = err?.response?.data?.message || err?.message || 'Failed to create API credential';
          setToastInfo({ message: errorMessage, toastType: 'error' });
          setOpen(true);
        },
      },
    );
  };

  const copyApiKey = () => {
    if (createdCredential?.apiKey) {
      navigator.clipboard.writeText(createdCredential.apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const envLine = createdCredential?.apiKey
    ? `REACT_APP_CHURNSIGNAL_API_KEY=${createdCredential.apiKey}`
    : '';

  const copyEnvLine = () => {
    if (!envLine) return;
    navigator.clipboard.writeText(envLine);
    setCopiedEnvLine(true);
    setTimeout(() => setCopiedEnvLine(false), 2000);
  };

  const handleApiKeyCopied = () => {
    handleClose();
    onSuccess();
  };

  // Show API key display modal if credential was created
  if (createdCredential) {
    return createPortal(
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]"
        onClick={handleApiKeyCopied}
      >
        <div
          className="bg-white rounded-lg p-6 w-[36rem] max-w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between mb-6">
            <h3 className="text-xl font-semibold text-custom-black">{t('APICredentialsPage.modalTitleSuccess')}</h3>
            <button
              onClick={handleApiKeyCopied}
              className="flex-shrink-0 text-custom-darkSilver hover:text-custom-black transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Check className="text-green-600" size={20} />
                <p className="text-sm font-medium text-green-800">{t('APICredentialsPage.modalSuccessMessage')}</p>
              </div>
              <p className="text-xs text-green-700">{t('APICredentialsPage.modalSuccessDescription')}</p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <InfoIcon width={16} color="#f59e0b" className="mt-0.5 flex-shrink-0" />
                <p className="text-xs text-yellow-800">⚠️ {t('APICredentialsPage.modalWarning')}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-custom-black mb-2">
                {t('APICredentialsPage.modalApiKeyLabel')}
              </label>
              <div className="flex items-center space-x-2">
                <code className="flex-1 bg-custom-silver text-custom-blue p-3 rounded-lg font-mono text-sm break-all border border-custom-lightSilver">
                  {createdCredential.apiKey}
                </code>
                <button
                  onClick={copyApiKey}
                  className="px-4 py-3 rounded-lg transition-colors bg-custom-blue hover:bg-custom-darkBlue text-white flex-shrink-0"
                  title={t('Common.copy')}
                >
                  {copied ? <Check size={20} /> : <Copy size={20} />}
                </button>
              </div>
              {copied && (
                <p className="mt-2 text-xs text-custom-blue flex items-center gap-2">
                  <InfoIcon width={14} color={brandColors.blue} /> {t('APICredentialsPage.modalCopiedMessage')}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-custom-black mb-2">
                {t('APICredentialsPage.modalEnvLineLabel')}
              </label>
              <p className="text-xs text-custom-darkSilver mb-2">{t('APICredentialsPage.modalEnvLineDescription')}</p>
              <div className="flex items-center space-x-2">
                <code className="flex-1 bg-custom-silver text-custom-blue p-3 rounded-lg font-mono text-sm break-all border border-custom-lightSilver">
                  {envLine}
                </code>
                <button
                  type="button"
                  onClick={copyEnvLine}
                  className="px-4 py-3 rounded-lg transition-colors bg-custom-blue hover:bg-custom-darkBlue text-white flex-shrink-0"
                  title={t('APICredentialsPage.modalCopyEnvLine')}
                >
                  {copiedEnvLine ? <Check size={20} /> : <Copy size={20} />}
                </button>
              </div>
              {copiedEnvLine && (
                <p className="mt-2 text-xs text-custom-blue flex items-center gap-2">
                  <InfoIcon width={14} color={brandColors.blue} /> {t('APICredentialsPage.modalEnvLineCopied')}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button onClick={handleApiKeyCopied} variant="primary">
                {t('Common.done')}
              </Button>
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  // Show creation form
  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg p-6 w-[36rem] max-w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <h3 className="text-xl font-semibold text-custom-black">{t('APICredentialsPage.modalTitleCreate')}</h3>
          <button
            onClick={handleClose}
            className="flex-shrink-0 text-custom-darkSilver hover:text-custom-black transition-colors"
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <Input
              label={t('APICredentialsPage.modalNameLabel')}
              placeholder={t('APICredentialsPage.modalNamePlaceholder')}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setErrors((prev) => ({ ...prev, name: undefined }));
              }}
              error={errors.name}
              disabled={isLoading}
              fullWidth
              maxLength={255}
            />
            <p className="mt-2 text-xs text-custom-darkSilver">{t('APICredentialsPage.modalNameDescription')}</p>
          </div>

          <div>
            <Input
              label={t('APICredentialsPage.modalExpirationLabel')}
              placeholder={t('APICredentialsPage.modalExpirationPlaceholder')}
              type="number"
              value={expirationDays}
              onChange={(e) => {
                setExpirationDays(e.target.value);
                setErrors((prev) => ({ ...prev, expirationDays: undefined }));
              }}
              error={errors.expirationDays}
              disabled={isLoading}
              fullWidth
            />
            <p className="mt-2 text-xs text-custom-darkSilver">{t('APICredentialsPage.modalExpirationDescription')}</p>
          </div>

          <div>
            <Textarea
              label={t('APICredentialsPage.modalDescriptionLabel')}
              placeholder={t('APICredentialsPage.modalDescriptionPlaceholder')}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setErrors((prev) => ({ ...prev, description: undefined }));
              }}
              error={errors.description}
              disabled={isLoading}
              maxCharacter={500}
              characterCount
              optional
            />
            <p className="mt-2 text-xs text-custom-darkSilver">{t('APICredentialsPage.modalDescriptionDescription')}</p>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button onClick={handleClose} variant="transparent" disabled={isLoading}>
              {t('Common.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={isLoading || !name.trim()} isLoading={isLoading}>
              {t('APICredentialsPage.modalCreateButton')}
            </Button>
          </div>
        </div>

        <ProvideToasts>
          <Toast
            isOpen={open}
            setOpen={setOpen}
            message={toastInfo.message}
            toastType={toastInfo.toastType as ToastType}
          />
        </ProvideToasts>
      </div>
    </div>,
    document.body,
  );
};

export default CreateAPICredentialModal;
